import React, { useContext, useEffect, useState, useCallback } from "react";
import i18next from "i18next";
import _ from "lodash";
import { Trans } from "react-i18next";
import { convert } from 'html-to-text';

import useStyles from "style/js-style/containers/administration/calculator/CalculatorStyle";
import DataTableButton from "components/common/datatable/DataTableButtons";
import CalculatorCreateDialog from "./dialogs/CalculatorCreateDialog";
import CalculatorEditDialog from "./dialogs/CalculatorEditDialog";
import CalculatorDeleteDialog from "./dialogs/CalculatorDeleteDialog";
import EAIcon from "components/common/EAIcon";
import { calculationListColumns, FORM_ID } from "./CalculatorDataTableUtils";
import { getAccountCalculations, toggleCalculationStatus } from "services/edge/CalculatorService";
import { Context } from "states/Store";
import { SUCCESS, WARNING, ERROR, Subscription, FREE_DOSSIER_TYPE_ID_VALUE } from "services/common/Constants";
import EADataGrid from "components/common/datatable/EADataGrid";
import { getAccountAttributesByBondType, getAccountCategorizedAttributes } from "services/edge/AttributesService";
import { getAccountRequirements } from "services/edge/AccountService";
import { CalculationNature, CalculationResultType, CalculatorContext } from "services/common/CalculatorUtils";
import {
  formatActivityTextFromActivity,
  getCountries,
  hasOption,
} from '../../../services/common/Utils';
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import { getFinancialStrengthScoreValuesForRulesAndCalculations } from "services/common/DataBlocksUtils";
import { getSourceDataConfigForAccount } from "services/edge/DataHubService";
import { getContextModel } from '../../../services/edge/ContexteService';
import { getCollectiveProceedingSteps } from "services/edge/ListService";

function CalculatorDataTable() {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const [activities, setActivities] = useState([]);
  const [allCountries, setAllCountries] = useState([]);

  const [thirdpartyAttributes, setThirdpartyAttributes] = useState([]);

  const [dossierAttributes, setDossierAttributes] = useState([]);
  const [dossierTypes, setDossierTypes] = useState([]);

  const [documentTypes, setDocumentTypes] = useState([]);

  const [calculatedAttributes, setCalculatedAttributes] = useState([]);
  const [thirdPartyIndicators, setThirdPartyIndicators] = useState([]);

  const [financialStrengthRatings, setFinancialStrengthRatings] = useState([]);
  const [financialStrengthScores, setFinancialStrengthScores] = useState([]);

  const [sourceData, setSourceData] = useState([]);

  const [sourceVariables, setSourceVariables] = useState([]);
  const [showInduedScores, setShowInduedScores] = useState(false);

  const [contextModels, setContextModels] = useState([]);
  const [collectiveProceedingSteps, setCollectiveProceedingSteps] = useState([]);

  // 'Show' has priority here
  const [showTransparencyScore, setShowTransparencyScore] = useState(false);
  const [enableTransparencyScore, setEnableTransparencyScore] = useState(false);

  const [showFinancialStrengthScores, setShowFinancialStrengthScores] = useState(false);
  const [showEsgScores, setShowEsgScores] = useState(false);
  const [showEcovadisScores, setShowEcovadisScores] = useState(false);
  const [showSecurityRatingScores, setShowSecurityRatingScores] = useState(false);
  const [showCreditsafeScores, setShowCreditsafeScores] = useState(false);

  const [showSourceData, setShowSourceData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [calculations, setCalculations] = useState([]);
  const [displayableCalculations, setDisplayableCalculations] = useState([]);
  const [activeCalculationCounter, setActiveCalculationCounter] = useState([]);
  const [selectedCalculation, setSelectedCalculation] = useState(undefined);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isReadDialogOpen, setIsReadOnlyOpen] = useState(false);

  // Fonction de tri par ordre alphabétique des labels
  const sortByLabel = (values) => {
    return values.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });
  }

  const fetchActivities = useCallback(async () => {
    try {
      setActivities(
        state.activityTraductions
          .filter((a) => a.origine === 'DNB')
          .map((activity) => {
            return {
              value: activity.code,
              label: formatActivityTextFromActivity(activity),
              level: activity.level,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label))
      );

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setActivities([]);
    }
  }, [state.activityTraductions, dispatch]);

  const fetchCountries = useCallback(async () => {
    try {

      const isoCountries = getCountries(i18next.language);

      setAllCountries(Object.keys(isoCountries).map((code) => {
        return {
          value: code,
          label: isoCountries[code]
        }
      }));

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setAllCountries([]);
    }
  }, [dispatch]);

  const fetchThirdPartyAttributes = useCallback(async () => {
    try {

      const [results, categorizedResults] = await Promise.all([
        getAccountAttributesByBondType(state.account.id, "thirdparty", null),
        getAccountCategorizedAttributes(state.account.id, null, "thirdparty", null)
      ]);

      setThirdpartyAttributes([...results.content, ...categorizedResults.content]);

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setThirdpartyAttributes([]);
    }
  }, [state.account, dispatch]);

  const fetchDossierAttributes = useCallback(async () => {
    try {

      const results = await getAccountAttributesByBondType(state.account.id, "dossier", null);
      setDossierAttributes(results.content);

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setDossierAttributes([]);
    }
  }, [state.account, dispatch]);

  const fetchSourceData = useCallback(async () => {
    try {
      const resSourceData = await getSourceDataConfigForAccount(state.account.id, dispatch);
      setSourceData(resSourceData.content);

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setSourceData([]);
    }
  }, [state.account, dispatch]);

  const fetchDocumentTypes = useCallback(async () => {
    try {

      const results = await getAccountRequirements(state.account.id, dispatch);
      setDocumentTypes(
        results.content.map((x) => {
          return {
            value: `${x?.documentId}`,
            label: x?.descriptionFr,
            properties: x?.properties?.map((property) => {
              return {
                id: property?.id,
                label: convert(property?.name),
                type: property?.type,
                valueOptions: property?.options || []
              };
            }) || [],
            ocrExtractedFields: x.ocrExtractedFields?.map((ocrField) => {
              return {
                id: ocrField?.name,
                label: ocrField?.label,
                type: ocrField?.type,
                valueOptions: []
              }
            })
          }
        })
      );

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setDocumentTypes([]);
    }
  }, [state.account, dispatch]);

  const fetchCalculations = useCallback(async () => {
    try {
      setIsLoading(true);

      const results = await getAccountCalculations(state.account.id, null, null);

      // Order list by calculation name
      const orderedResults = results.content.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      setCalculations(orderedResults);

      setDisplayableCalculations(orderedResults.map((entry) => {
        return {
          ...entry,
          nature: i18next.t(`calculator.dialog.create.natureOption.${entry.nature}`),
          executionMode: i18next.t(`calculator.dialog.create.executionModeOption.${entry.executionMode}`),
          resultType: i18next.t(`calculator.dialog.create.resultTypeOption.${entry.resultType}`)
        };
      }));

      setCalculatedAttributes(_.filter(orderedResults, { nature: CalculationNature.CALCULATED_ATTRIBUTE, enabled: true }).map((entry) => {
        return {
          ...entry,
          value: `${entry?.id}`,
          label: entry?.name,
          type: entry?.resultType,
          options: [CalculationResultType.STRING].includes(entry?.resultType) ? entry?.branches?.map((branch) => (branch?.resultValue)) : []
        };
      }));

      setThirdPartyIndicators(_.filter(orderedResults, { nature: CalculationNature.INDICATOR, enabled: true }).map((entry) => {
        return {
          ...entry,
          value: `${entry?.id}`,
          label: entry?.name,
          type: entry?.resultType,
          options: [CalculationResultType.STRING].includes(entry?.resultType) ? entry?.branches?.map((branch) => (branch?.resultValue)) : []
        };
      }));

      setSourceVariables(_.filter(orderedResults, { nature: CalculationNature.SOURCE_VARIABLE, enabled: true }).map((entry) => {
        return {
          ...entry,
          value: `${entry?.id}`,
          label: entry?.name,
          type: entry?.resultType,
          options: [CalculationResultType.STRING].includes(entry?.resultType) ? entry?.branches?.map((branch) => (branch?.resultValue)) : []
        };
      }));

      setActiveCalculationCounter(orderedResults.filter(entry => entry.enabled).length)

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setCalculations([]);
    }
  }, [state.account, dispatch]);

  const fetchContextModels = useCallback(async () => {
    try {
      const contextModels = await getContextModel(state.account.id, dispatch);
      setContextModels(contextModels.filter(model => model.active));
    } catch(e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setContextModels([]);
    }
  }, [state.account, dispatch]);

  const fetchCollectiveProceedingSteps = useCallback(async () => {
    try {
      const results = await getCollectiveProceedingSteps(dispatch);
      setCollectiveProceedingSteps(results.content);

    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setCollectiveProceedingSteps([]);
    }
  }, [dispatch]);

  useEffect(() => {
    state.activityTraductions.length && fetchActivities();
  }, [fetchActivities, state.activityTraductions]);

  useEffect(() => {
    fetchCountries();
    fetchCollectiveProceedingSteps();
  }, [fetchCountries, fetchCollectiveProceedingSteps]);


  useEffect(() => {
    fetchThirdPartyAttributes();
    fetchDocumentTypes();
    fetchCalculations();
    fetchDossierAttributes();
    fetchSourceData();
    fetchContextModels();
  }, [state.account, fetchThirdPartyAttributes,
    fetchDocumentTypes, fetchCalculations, fetchDossierAttributes,
    fetchSourceData, fetchContextModels
  ]);


  useEffect(() => {
    const mappedDossierTypes = [
      {
        value: `${FREE_DOSSIER_TYPE_ID_VALUE}`,
        label: i18next.t("free")
      }
    ];

    if (state.dossierTypes && !_.isEmpty(state.dossierTypes)) {
      state.dossierTypes.forEach((dt) => {
        mappedDossierTypes.push({
          value: `${dt.id}`,
          label: dt.libelle
        });
      });
    }

    setDossierTypes(mappedDossierTypes);
  }, [state.dossierTypes]);

  useEffect(() => {
    if (!_.isEmpty(state.referenceData)) {
      setFinancialStrengthRatings(
        state.referenceData.financialStrengthRating
          ?.map((x) => ({
            label: x.rating,
            value: x.rating,
          }))
          ?.sort((a, b) => a.label.localeCompare(b.label))
      );
      setFinancialStrengthScores(getFinancialStrengthScoreValuesForRulesAndCalculations());
    }
  }, [state.referenceData])

  useEffect(() => {
    setShowInduedScores(
      hasOption(state.accounts, state.account.id, Subscription.INDUED) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE)
    )
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    const bTransparencyOptionActive = hasOption(state.accounts, state.account.id, Subscription.TRANSPARENCY);
    setShowTransparencyScore(bTransparencyOptionActive);

    if (bTransparencyOptionActive) {
      setEnableTransparencyScore(hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE))
    } else {
      setEnableTransparencyScore(true);
    }
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    setShowFinancialStrengthScores(
      hasOption(state.accounts, state.account.id, Subscription.DATABLOCKS_FINANCIAL_STRENGTH) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE)
    )
  }, [hasAnyOfPrivileges, state.account.id, state.accounts])

  useEffect(() => {
    setShowEsgScores(
      hasOption(state.accounts, state.account.id, Subscription.DATABLOCKS_ESG) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE)
    )
  }, [hasAnyOfPrivileges, state.account.id, state.accounts])

  useEffect(() => {
    setShowEcovadisScores(
      hasOption(state.accounts, state.account.id, Subscription.ECOVADIS) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE)
    )
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    setShowSecurityRatingScores(
      hasOption(state.accounts, state.account.id, Subscription.SECURITY_RATING) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE)
    )
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    setShowCreditsafeScores(
      hasOption(state.accounts, state.account.id, Subscription.CREDITSAFE) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE)
    )
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    setShowSourceData(
      hasOption(state.accounts, state.account.id, Subscription.DATA_HUB) && 
              hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DATA_HUB_SOURCE)
    )
  }, [state.account.id, state.accounts, hasAnyOfPrivileges])

  const context = React.useMemo(
    () => ({
      selectedCalculation: selectedCalculation,
      activities: sortByLabel(activities),
      countries: sortByLabel(allCountries),
      thirdpartyAttributes: sortByLabel(thirdpartyAttributes),
      dossierAttributes: sortByLabel(dossierAttributes),
      dossierTypes: sortByLabel(dossierTypes),
      documentTypes: sortByLabel(documentTypes),
      calculatedAttributes: sortByLabel(calculatedAttributes),
      thirdPartyIndicators: sortByLabel(thirdPartyIndicators),
      financialStrengthScores: financialStrengthScores,
      financialStrengthRatings: financialStrengthRatings,
      sourceData: sourceData,
      sourceVariables: sortByLabel(sourceVariables),
      calculations: calculations,
      showInduedScores: showInduedScores,
      showTransparencyScore: showTransparencyScore,
      enableTransparencyScore: enableTransparencyScore,
      showFinancialStrengthScores: showFinancialStrengthScores,
      showEsgScores: showEsgScores,
      showEcovadisScores: showEcovadisScores,
      showSecurityRatingScores: showSecurityRatingScores,
      showCreditsafeScores: showCreditsafeScores,
      showSourceData: showSourceData,
      lang: state.lang,
      contextModels: contextModels,
      collectiveProceedingSteps: collectiveProceedingSteps,
    }),
    [
      selectedCalculation,
      activities,
      allCountries,
      thirdpartyAttributes,
      dossierAttributes,
      dossierTypes,
      documentTypes,
      calculatedAttributes,
      thirdPartyIndicators,
      financialStrengthScores,
      financialStrengthRatings,
      sourceData,
      sourceVariables,
      calculations,
      showInduedScores,
      showTransparencyScore,
      showSourceData,
      showFinancialStrengthScores,
      showEsgScores,
      enableTransparencyScore,
      showEcovadisScores,
      showSecurityRatingScores,
      showCreditsafeScores,
      state.lang,
      contextModels,
      collectiveProceedingSteps
    ]
  );

  /** Recherche et sélectionne le calcul actif */
  const setSelectedById = useCallback((id) => {
    setSelectedCalculation(_.find(calculations, { id: id }));
  }, [calculations]);

  const openCreateCalculation = useCallback(() => {
    setSelectedCalculation(undefined);
    setIsCreateDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((id) => {
    setSelectedById(id);
    setIsDeleteDialogOpen(true);
  }, [setSelectedById]);

  const openEditDialog = useCallback((id) => {
    setSelectedById(id);
    setIsEditDialogOpen(true);
  }, [setSelectedById]);

  const openReadDialog = useCallback((id) => {
    setSelectedById(id);
    setIsReadOnlyOpen(true);
  }, [setSelectedById]);

  /** Activation / Désactivation du calcul */
  const onToggleCalculationStatus = useCallback(async (calculationId) => {
    try {
      let calculationToUpdate = _.find(calculations, { id: calculationId });
      await toggleCalculationStatus(state.account.id, calculationId, dispatch);

      // Notification
      const name = calculationToUpdate.name;
      const type = calculationToUpdate.enabled ? WARNING : SUCCESS;
      const message = calculationToUpdate.enabled
        ? <Trans i18nKey="calculator.notifications.deactivationConfirmation"> Le calcul <span style={{ color: "#0B2810", fontWeight: 600 }}>"{{ name }}"</span> a bien été activé.</Trans>
        : <Trans i18nKey="calculator.notifications.activationConfirmation"> Le calcul <span style={{ color: "#0B2810", fontWeight: 600 }}>"{{ name }}"</span> a bien été désactivé.</Trans>

      dispatch({
        type: "ALERT",
        alert: { type: type, msg: message },
      });

      fetchCalculations();
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
    }
  }, [state.account, calculations, fetchCalculations, dispatch]);

  return (
    <CalculatorContext.Provider value={context}>
      {/* Header */}
      <div className={classes.headerWrapper}>
        <div className={classes.headerMetaWrapper}>
          <EAIcon icon={"calculate"} className={classes.metaLabelIcon} />
          <span className={classes.headerMetaInfo}>{activeCalculationCounter} {activeCalculationCounter > 1 ? i18next.t("calculator.activeCalculations") : i18next.t("calculator.activeCalculation")}</span>
        </div>
        <DataTableButton
          onClickHandler={() => openCreateCalculation()}
          label={i18next.t("calculator.createCalculation")}
          disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CALCULATION)}
        />
      </div>
      <div className={classes.separate} />
      {/* DataGrid */}
      <div className={classes.dataGridWrapper} >
        <EADataGrid
          loading={isLoading}
          columns={calculationListColumns(openEditDialog, openReadDialog, openDeleteDialog, onToggleCalculationStatus, hasAnyOfPrivileges)}
          inputRows={displayableCalculations}
          pageSize={10}
          hasCustomPagination
          additionnalsLeftComponents={<div className={classes.dataGridTitle}>{i18next.t("calculator.datagridTitle")}</div>}
        />
      </div>

      {/* PopUp : on create */}
      {isCreateDialogOpen && (
        <CalculatorCreateDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onValidateSuccess={() => {
            setIsCreateDialogOpen(false);
            fetchCalculations();
          }}
          formId={FORM_ID.create}
        />
      )}

      {/* PopUp : on edit */}
      {(isEditDialogOpen || isReadDialogOpen) && (
        <CalculatorEditDialog
          calculation={selectedCalculation}
          isOpen={isEditDialogOpen || isReadDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setIsReadOnlyOpen(false);
          }}
          onDelete={() => {
            setIsEditDialogOpen(false);
            openDeleteDialog(selectedCalculation.id);
          }}
          onValidateSuccess={() => {
            setIsEditDialogOpen(false);
            fetchCalculations();
          }}
          formId={FORM_ID.edit}
          readOnly={isReadDialogOpen}
        />
      )}

      {/* PopUp : on delete */}
      {isDeleteDialogOpen && (
        <CalculatorDeleteDialog
          calculation={selectedCalculation}
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onValidateSuccess={() => {
            setIsDeleteDialogOpen(false);
            setIsEditDialogOpen(false);
            fetchCalculations();
          }}
        />
      )}
    </CalculatorContext.Provider>
  )
}

export default CalculatorDataTable;
